import React, { useMemo } from "react"
import type { FC } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation } from "react-router"

import { NzDataGrid, defaultRowHeight } from "@/components/nzDataGrid"
import { PageCard } from "@/components/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganizationId } from "@/contexts"
import type { Site } from "@/models/site"
import {
  createAdminGridEditAction,
  createAdminGridViewAction,
  useAdminGridActionsColumn,
} from "@/modules/admin/components/admin-grid-actions-column"
import { formatAddress } from "@/modules/admin/sites"
import {
  DataGridFill,
  DataGridFilterPanel,
  DataGridToolbar,
  baseColumnDef,
  gridUrlValuesKey,
  useAdjustScrollOnPrepend,
  useDataGridVirtualScrollListener,
  useGridUrlState,
  useInfiniteScroll,
  useServerMappedStringFilters,
  useSyncInfiniteQueryPage,
  withGridUrlUpdates,
} from "@/nzds/data-grid"
import { useSitesInfinite } from "@/services/site/use-sites-infinite"
import { useSiteNameColumn } from "@/utils/grid-columns/use-site-name-column"
import { usePage } from "@/utils/page/use-page"
import { useSavePage } from "@/utils/page/use-page/use-save-page"
import { useUrl } from "@/utils/url"

import { Link as MuiLink, Stack } from "@mui/material"
import type {
  GridColDef,
  GridFilterModel,
  GridSlotProps,
  GridSlots,
} from "@mui/x-data-grid-pro"

import {
  ADMIN_SITES_GRID_URL_KEYS,
  getAppliedAdminSitesFilters,
  getNextAdminSitesGridUrlUpdate,
  readAdminSitesGridUrl,
  toAdminSitesFilterModel,
} from "./admin-sites-grid-url"

interface SiteGridRecord {
  address: string
  id: string
  name: Site["name"]
}

const siteToGridRecord = (site: Site): SiteGridRecord => ({
  address: formatAddress(site.address),
  id: String(site.id),
  name: site.name ?? "",
})

const syncAdminSitesGridFromUrl = ({
  appliedSignatureRef,
  criteria,
  filterSignature,
  gridUrlValues,
  lastWrittenKeyRef,
  logicOperator,
  pageParam,
  replaceFilterModelRef,
  replaceGridUrlState,
  restoredPage,
  seededKeyRef,
  skipSignatureChangeRef,
}: {
  appliedSignatureRef: { current: string }
  criteria: readonly { field: string; operator: string; value?: string }[]
  filterSignature: string
  gridUrlValues: Record<string, string | null>
  lastWrittenKeyRef: { current: string | null }
  logicOperator: string
  pageParam: string | null
  replaceFilterModelRef: { current: (model: GridFilterModel) => void }
  replaceGridUrlState: (updates: Record<string, string | null>) => void
  restoredPage: string
  seededKeyRef: { current: string }
  skipSignatureChangeRef: { current: boolean }
}): void => {
  const currentKey = gridUrlValuesKey(ADMIN_SITES_GRID_URL_KEYS, gridUrlValues)

  if (currentKey === lastWrittenKeyRef.current) {
    lastWrittenKeyRef.current = null
    seededKeyRef.current = currentKey
    return
  }

  // The Sites nav clears this route's query without remounting the page.
  // Copy that address into the filter model before publishing panel changes.
  if (currentKey !== seededKeyRef.current) {
    seededKeyRef.current = currentKey
    skipSignatureChangeRef.current = true
    replaceFilterModelRef.current(
      toAdminSitesFilterModel(readAdminSitesGridUrl(gridUrlValues))
    )
    return
  }

  // Compare to the last synced applied filter, not the first render. Clearing
  // or returning to the opened filter is still a person changing the filter.
  const signatureChanged =
    !skipSignatureChangeRef.current &&
    filterSignature !== appliedSignatureRef.current
  appliedSignatureRef.current = filterSignature
  skipSignatureChangeRef.current = false

  const updates = getNextAdminSitesGridUrlUpdate({
    applied: getAppliedAdminSitesFilters(criteria, logicOperator),
    pageParam,
    restoredPage,
    signatureChanged,
    values: gridUrlValues,
  })
  if (!updates) {
    return
  }

  lastWrittenKeyRef.current = gridUrlValuesKey(
    ADMIN_SITES_GRID_URL_KEYS,
    withGridUrlUpdates(gridUrlValues, updates)
  )
  replaceGridUrlState(updates)
}

const getRestoredListPage = (savedPage: number | null): string => {
  if (savedPage != null && Number.isFinite(savedPage) && savedPage > 0) {
    return String(savedPage)
  }

  return "1"
}

const useSitesGridColumns = (): GridColDef<SiteGridRecord>[] => {
  const { t } = useTranslation("common")
  const { search } = useLocation()
  const { adminSitePath } = useUrl()
  const siteNameColumn = useSiteNameColumn<SiteGridRecord>({
    field: "name",
    filterable: true,
  })

  return [
    {
      ...siteNameColumn,
      renderCell: ({ row, value }) => (
        <MuiLink component={Link} to={`${adminSitePath(row.id)}${search}`}>
          {value}
        </MuiLink>
      ),
    },
    {
      ...baseColumnDef,
      field: "id",
      filterable: false,
      headerName: t("admin.sitesGrid.id"),
      valueFormatter: (value) => value ?? "",
      width: 120,
    },
    {
      ...baseColumnDef,
      field: "address",
      filterable: true,
      headerName: t("admin.sitesGrid.address"),
      flex: 1,
    },
  ]
}

export const AdminSitesRoute: FC = () => {
  const { t } = useTranslation("common")
  const { search } = useLocation()
  const { activeOrganizationId } = useActiveOrganizationId()
  const { adminSiteEditPath, adminSitePath } = useUrl()
  const gridRootRef = React.useRef<HTMLDivElement | null>(null)
  const { replaceGridUrlState, values: gridUrlValues } = useGridUrlState(
    ADMIN_SITES_GRID_URL_KEYS
  )
  const [initialFilterModel] = React.useState(() =>
    toAdminSitesFilterModel(readAdminSitesGridUrl(gridUrlValues))
  )
  const isSitesQueryEnabled = Boolean(activeOrganizationId)
  const {
    criteria,
    filterModel,
    filterSignature,
    logicOperator,
    replaceFilterModel,
    setFilterModel,
    whereClause: siteNameWhereClause,
  } = useServerMappedStringFilters({
    fieldToApiField: { address: "site_address", name: "name" },
    fields: ["name", "address"],
    initialFilterModel,
    mode: "multiple",
    orFilterKey: "string_filters_or",
  })
  const { page, setPage } = usePage({
    id: "adminSitesPage",
  })
  const { savedPage } = useSavePage({ id: "adminSitesPage" })
  const appliedSignatureRef = React.useRef(filterSignature)
  const seededGridUrlKeyRef = React.useRef(
    gridUrlValuesKey(ADMIN_SITES_GRID_URL_KEYS, gridUrlValues)
  )
  const lastWrittenGridUrlKeyRef = React.useRef<string | null>(null)
  const replaceFilterModelRef = React.useRef(replaceFilterModel)
  React.useLayoutEffect(() => {
    replaceFilterModelRef.current = replaceFilterModel
  })
  const skipSignatureChangeRef = React.useRef(false)
  const pageParam = new URLSearchParams(search).get("page")

  React.useEffect(() => {
    syncAdminSitesGridFromUrl({
      appliedSignatureRef,
      criteria,
      filterSignature,
      gridUrlValues,
      lastWrittenKeyRef: lastWrittenGridUrlKeyRef,
      logicOperator,
      pageParam,
      replaceFilterModelRef,
      replaceGridUrlState,
      restoredPage: getRestoredListPage(savedPage),
      seededKeyRef: seededGridUrlKeyRef,
      skipSignatureChangeRef,
    })
  }, [
    criteria,
    filterSignature,
    gridUrlValues,
    logicOperator,
    pageParam,
    replaceGridUrlState,
    savedPage,
  ])

  const {
    sitesData,
    sitesFetchNextPage,
    sitesFetchPreviousPage,
    sitesHasNextPage,
    sitesHasPreviousPage,
    sitesIsFetching,
    sitesIsFetchingNextPage,
    sitesIsFetchingPreviousPage,
    sitesIsPlaceholderData,
  } = useSitesInfinite({
    apiQueryOptions: {
      extraFields: ["building_type", "sq_ft"],
      includes: ["address"],
      where: {
        organization_id: activeOrganizationId,
        ...siteNameWhereClause,
      },
    },
    queryOptions: {
      enabled: isSitesQueryEnabled,
      throwOnError: false,
    },
    initialPage: page,
  })

  const sites = useMemo(() => {
    if (!sitesData) {
      return []
    }
    return sitesData.pages.flatMap((pageData) => pageData.items)
  }, [sitesData])

  const pages = sitesData?.pages ?? []

  useSyncInfiniteQueryPage({
    page,
    pages,
    setPage,
  })

  const actionsColumn = useAdminGridActionsColumn<SiteGridRecord>({
    getActions: ({ id }) => {
      const siteId = String(id)
      return [
        createAdminGridViewAction(
          `${adminSitePath(siteId)}${search}`,
          t("button.view")
        ),
        createAdminGridEditAction(
          `${adminSiteEditPath(siteId)}${search}`,
          t("button.edit")
        ),
      ]
    },
    minWidth: 120,
    width: 120,
  })
  const columns = [...useSitesGridColumns(), actionsColumn]
  const {
    handleOnRowsScrollEnd,
    handleOnScrollTopChange,
    handleOnWheelUpAtTop,
  } = useInfiniteScroll({
    fetchNextPage: sitesFetchNextPage,
    fetchPreviousPage: sitesFetchPreviousPage,
    hasNextPage: sitesHasNextPage,
    hasPreviousPage: sitesHasPreviousPage,
    isEnabled: isSitesQueryEnabled,
    isFetchingNextPage: sitesIsFetchingNextPage,
    isFetchingPreviousPage: sitesIsFetchingPreviousPage,
  })

  const { adjustScrollTopBy } = useDataGridVirtualScrollListener({
    gridRootRef,
    onScroll: handleOnScrollTopChange,
    onWheelUpAtTop: handleOnWheelUpAtTop,
  })

  const rows: SiteGridRecord[] = useMemo(() => {
    if (!sites.length) {
      return []
    }
    return sites.map(siteToGridRecord)
  }, [sites])

  useAdjustScrollOnPrepend({
    adjustScrollTopBy,
    isFetchingPreviousPage: sitesIsFetchingPreviousPage,
    rowCount: rows.length,
    rowHeight: defaultRowHeight,
  })

  return (
    <Stack sx={{ gap: 2, height: "100%", minHeight: 0 }}>
      <PageTitle heading={t("admin.sites")} />
      <PageCard
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <DataGridFill ref={gridRootRef}>
          <NzDataGrid
            aria-label={t("admin.sitesGrid.ariaLabel")}
            columns={columns}
            disableColumnSorting
            filterModel={filterModel}
            getRowId={(row: SiteGridRecord) => row.id}
            filterMode="server"
            loading={
              sitesIsFetching ||
              sitesIsFetchingNextPage ||
              sitesIsFetchingPreviousPage
            }
            onFilterModelChange={setFilterModel}
            onRowsScrollEnd={handleOnRowsScrollEnd}
            initialState={{ pinnedColumns: { right: ["actions"] } }}
            rows={rows}
            slotProps={{
              toolbar: {
                isRecordCountLoading: sitesIsFetching || sitesIsPlaceholderData,
                recordCount: sitesData?.pages[0]?.total,
                showFilters: true,
              } as GridSlotProps["toolbar"],
            }}
            slots={{
              filterPanel: DataGridFilterPanel as GridSlots["filterPanel"],
              toolbar: DataGridToolbar as GridSlots["toolbar"],
            }}
            sortingMode="server"
          />
        </DataGridFill>
      </PageCard>
    </Stack>
  )
}
